import { Plus, Trash2 } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";

export type OptionRow = {
  /** Present for options that already exist, so editing keeps their identity. */
  id?: string;
  name: string;
  /** Kept as text while typing; validated by serializeOptions. */
  price: string;
};

export const MAX_OPTIONS = 20;

/** Rows as returned by the API (price is a decimal string). */
export function optionsFromApi(
  options?: { id: string; name: string; price: string | number }[],
): OptionRow[] {
  return (options || []).map((option) => ({
    id: option.id,
    name: option.name,
    price: String(Number(option.price)),
  }));
}

/**
 * Turn the editor rows into the JSON the API expects. Completely blank rows are dropped.
 * Returns an error message for rows that can't be saved, so the form can block the submit.
 */
export function serializeOptions(rows: OptionRow[]): {
  json: string | null;
  error: string | null;
} {
  const cleaned = [];
  for (const row of rows) {
    const name = row.name.trim();
    const price = row.price.trim();
    if (!name && !price) continue;
    if (!name) return { json: null, error: "Every option needs a name." };
    const amount = price === "" ? 0 : Number(price.replace(/,/g, ""));
    if (!Number.isFinite(amount) || amount < 0) {
      return { json: null, error: `The price for "${name}" must be 0 or more.` };
    }
    cleaned.push({ id: row.id, name, price: amount });
  }
  const names = cleaned.map((option) => option.name.toLowerCase());
  if (new Set(names).size !== names.length) {
    return { json: null, error: "Two options have the same name." };
  }
  return { json: JSON.stringify(cleaned), error: null };
}

type Props = {
  value: OptionRow[];
  onChange: (rows: OptionRow[]) => void;
};

/** Add choices to a product, e.g. "With chicken +₦1000". Customers pick one when ordering. */
export default function ProductOptionsEditor({ value, onChange }: Props) {
  const update = (index: number, changes: Partial<OptionRow>) =>
    onChange(value.map((row, i) => (i === index ? { ...row, ...changes } : row)));

  return (
    <div className="space-y-3">
      <p className="text-sm text-default-500">
        Optional. Customers choose one option when ordering, and its price is added to the
        product price. Use 0 for options that cost nothing extra.
      </p>

      {value.map((row, index) => (
        <div key={row.id ?? `new-${index}`} className="flex items-center gap-2">
          <Input
            className="flex-1"
            placeholder="Option, e.g. With chicken"
            value={row.name}
            maxLength={100}
            onChange={(e) => update(index, { name: e.target.value })}
          />
          <Input
            className="w-32"
            type="number"
            min={0}
            placeholder="+ Price (₦)"
            value={row.price}
            onChange={(e) => update(index, { price: e.target.value })}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-none"
            aria-label="Remove option"
            onClick={() => onChange(value.filter((_, i) => i !== index))}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      {value.length < MAX_OPTIONS && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          onClick={() => onChange([...value, { name: "", price: "" }])}
        >
          <Plus className="h-4 w-4" /> Add option
        </Button>
      )}
    </div>
  );
}
